import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { LinearProgress, Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import Drawer from '..';
import DrawerContent from '../content';
import DrawerHeader from '../header';
import { DrawerProps } from '../helpers';
import Text from '@/components/text';
import Icon from '@/components/icon';
import Button from '@/components/button';
import { DtFilter } from '@/components/data-table/helpers';
import { downloadTelemetry } from '@/lib/services/telemetry.service';
import { getNiceFilesize, LoadState } from '@/lib/helpers';
import { useQuery } from 'react-query';
import { QueryKey } from '@/lib/query-client';
import Card from '@/components/card';
import DrawerFooter from '../footer';
import FileIconImage from '@/assets/file-icon.svg';
import classNames from 'classnames';
import { AxiosProgressEvent } from 'axios';
import InlineToast from '@/components/toasts/inline';

const useStyles = createUseStyles((theme: Theme) => ({
  filename: {
    wordBreak: 'break-all',
  },
  progressTrack: {
    height: 8,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.lightGray,
    '& .MuiLinearProgress-bar': {
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.primary.main,
      transition: 'transform 300ms linear',
    },
  },
  progressTrackDone: {
    '& .MuiLinearProgress-bar': {
      backgroundColor: theme.palette.success.main,
    },
  },
  progressTrackCanceled: {
    '& .MuiLinearProgress-bar': {
      backgroundColor: theme.palette.gray,
    },
  },
}));

interface Props extends DrawerProps {
  filter: DtFilter;
}

const UserEventExportDrawer: FC<Props> = ({ open, onClose, className, isTopDrawer, filter }) => {
  const styles = useStyles();
  const [loadState, setLoadState] = useState<LoadState>('unloaded');
  const [downloadSize, setDownloadSize] = useState(0);
  const [downloadError, setDownloadError] = useState('');

  const filename = useMemo(() => {
    const dateForFilename = new Date().toISOString().replace(/:/g, '-').replace(/\./g, '-');
    return `f5-workforce-ai-security-user-events-export-${dateForFilename}.csv`;
  }, []);

  const controller = useRef(new AbortController());

  const isDone = loadState === 'loaded';
  const isLoading = loadState === 'loading';
  const isFailed = loadState === 'failed';

  const handleClose = () => {
    handleCancel();
    setTimeout(onClose, 500);
  };

  const handleCancel = () => {
    controller.current.abort();
    setLoadState('failed');
  };

  useQuery(
    [QueryKey.TelemetryExport],
    async () => {
      const onDownloadProgress = ({ loaded }: AxiosProgressEvent) => {
        setDownloadSize(loaded);
      };

      const { downloaded, aborted } = await downloadTelemetry(
        filter,
        filename,
        onDownloadProgress,
        controller.current
      );

      if (!downloaded) {
        if (!aborted) {
          setDownloadError(
            'The download failed. Please close the drawer and try again in a few moments.'
          );
        }
        setLoadState('failed');
        return;
      }

      setLoadState('loaded');
    },
    { enabled: isLoading }
  );

  useEffect(() => {
    if (loadState !== 'unloaded') {
      return;
    }
    setLoadState('loading');
  }, [loadState]);

  // cancel the download if the drawer closes
  useEffect(() => {
    if (loadState === 'loading' && !open) {
      controller.current.abort();
    }
  }, [open, loadState]);

  const hasError = !!downloadError;

  const trackClass = classNames(styles.progressTrack, {
    [styles.progressTrackDone]: isDone,
    [styles.progressTrackCanceled]: isFailed,
  });

  const niceDownloadSize = getNiceFilesize(downloadSize);

  return (
    <Drawer open={open} onClose={handleClose} className={className} size="small">
      <DrawerHeader onClose={handleClose} isTopDrawer={isTopDrawer} hideMenu>
        User Activity Export
      </DrawerHeader>
      <DrawerContent>
        <Stack pt={1} justifyContent="space-between" height="100%">
          <Stack gap={3}>
            <Text size="small">
              The requested export will start downloading when ready. All exports are prepared as
              CSVs using the current filter set, and have a limit of 100,000 records. Please note
              that exports may take up to several minutes to prepare, depending on the number of
              requested events.
            </Text>

            <Card>
              <Stack direction="row" alignItems="flex-start" gap={2} mb={2}>
                <img src={FileIconImage} width={40} />
                <Text color="black" className={styles.filename}>
                  {filename}
                </Text>
              </Stack>

              <Stack gap={1}>
                <LinearProgress
                  variant={isDone || isFailed ? 'determinate' : 'indeterminate'}
                  value={100}
                  className={trackClass}
                />
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Icon
                      name={isFailed ? 'close' : 'check-circle'}
                      color={isDone ? 'success' : 'grey'}
                      size="small"
                    />
                    <Text size="x-small" color={isDone ? 'success' : 'grey'}>
                      {niceDownloadSize}
                    </Text>
                  </Stack>
                </Stack>
              </Stack>
            </Card>
            {hasError && <InlineToast level="error" message={downloadError} />}
          </Stack>

          <DrawerFooter>
            {isLoading ? (
              <Button type="cancel" label="Cancel" onClick={handleCancel} fullWidth />
            ) : (
              <Button
                type="ok"
                label={isFailed ? 'Close' : 'Done'}
                onClick={() => onClose()}
                fullWidth
              />
            )}
          </DrawerFooter>
        </Stack>
      </DrawerContent>
    </Drawer>
  );
};

export default UserEventExportDrawer;
